import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { TFunction } from 'i18next';
import React, { FC } from 'react';
import { formatMoney } from 'utils/helpers';
import { AccountedBy } from 'v2-types/order';
import clsx from 'clsx';

const useStyles = makeStyles((theme: any) => ({
  container: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  mainContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
    width: '100%',
  },
  containerInfo: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'space-between',
  },
  infoName: {
    alignItems: 'center',
    display: 'flex',
    gap: '3px',
  },
  text: {
    textTransform: 'capitalize',
    color: theme.palette.primary.darker,
    fontSize: 14,
  },
  kind: {
    textTransform: 'capitalize',
    color: theme.palette.secondary.main,
    fontSize: 14,
    margin: '0 5px',
  },
  kindPosted: {
    color: theme.palette.primary.darker,
  },
  amount: {
    textTransform: 'capitalize',
    color: theme.palette.error.main,
    fontSize: 14,
    fontWeight: 'bolder',
    margin: '0 5px',
  },
  amountPosted: {
    color: theme.palette.primary.darker,
  },
  amountInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  footerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  diffContainer: {
    display: 'flex',
    gap: 10,
  },
  btn: {
    textTransform: 'capitalize',
    color: theme.palette.error.main,
  },
}));

type Props = {
  accounted: AccountedBy[] | undefined;
  t: TFunction;
  posted: boolean | undefined;
};

const DisbursmentsAccountedCell: FC<Props> = ({ accounted, t, posted }) => {
  const classes = useStyles();

  return (
    <>
      {accounted?.map((item) => {
        const { amount, record } = item;
        const { letter, number, _id, amount_faked } = record;
        const {
          accounted_amount = 0,
          name = '',
          order_kinds = [],
          kind = '',
          amount: amountRecord = 0,
        } = item.record?.involved_parties?.[0] || {};
        const orderKinds = order_kinds.sort((a, b) => a.localeCompare(b));

        const diffAmounts = amountRecord - accounted_amount;

        const hasAmountFaked = amount_faked !== undefined && amount_faked > 0;

        return (
          <Box className={classes.container} key={_id}>
            <Box className={classes.mainContainer}>
              <Box className={classes.containerInfo}>
                <Box className={classes.infoName}>
                  <Typography>
                    <span className={classes.text}>{item.record.description}</span>
                    {letter && number && (
                      <span className={classes.amount}>{`(${letter}-${number})`}</span>
                    )}
                    <span className={classes.text}>{name}</span>
                    <span className={classes.kind}>{`(${kind})`}</span>
                  </Typography>
                </Box>
                <Box className={classes.amountInfo}>
                  <Typography
                    className={clsx({
                      [classes.kind]: true,
                      [classes.kindPosted]: !posted,
                    })}
                  >
                    {formatMoney(amount)}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.footerContainer}>
                <Typography className={classes.text}>{orderKinds.join(', ')}</Typography>
                {diffAmounts > 0 && !hasAmountFaked && (
                  <Box className={classes.diffContainer}>
                    <Typography
                      className={clsx({
                        [classes.kind]: true,
                        [classes.kindPosted]: !posted,
                      })}
                    >
                      {t('disbursments:rest')}
                    </Typography>
                    <Typography
                      className={clsx({
                        [classes.amount]: true,
                        [classes.amountPosted]: !posted,
                      })}
                    >
                      {formatMoney(diffAmounts)}
                    </Typography>
                  </Box>
                )}
                {hasAmountFaked && (
                  <Box className={classes.diffContainer}>
                    <Typography
                      className={clsx({
                        [classes.amount]: true,
                        [classes.amountPosted]: !posted,
                      })}
                    >
                      {formatMoney(amount_faked)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
        );
      })}
    </>
  );
};

export default DisbursmentsAccountedCell;
